import { getItem, setItem, type LocalStorageKeys } from 'owa-local-storage';
import { setRewardsPanelOpenedMutator } from '../mutators/setRewardsPanelOpened';

const SESSION_INTERVAL = 5;
const MAX_IMPRESSIONS = 3;

interface RewardsTipState {
    hasOpenedRewards: boolean;
    impressionCount: number;
    lastImpressionSession: number;
}

const defaultState: RewardsTipState = {
    hasOpenedRewards: false,
    impressionCount: 0,
    lastImpressionSession: 0,
};

function getStorageKey(accountKey: string): LocalStorageKeys {
    return `RewardsTipState_${accountKey}`;
}

function getState(accountKey: string): RewardsTipState {
    const storedValue = getItem(window, getStorageKey(accountKey));
    if (!storedValue) {
        return defaultState;
    }

    try {
        const parsedValue = JSON.parse(storedValue) as Partial<RewardsTipState>;
        return {
            hasOpenedRewards: parsedValue.hasOpenedRewards === true,
            impressionCount:
                typeof parsedValue.impressionCount === 'number' ? parsedValue.impressionCount : 0,
            lastImpressionSession:
                typeof parsedValue.lastImpressionSession === 'number'
                    ? parsedValue.lastImpressionSession
                    : 0,
        };
    } catch {
        return defaultState;
    }
}

function setState(accountKey: string, state: RewardsTipState): void {
    setItem(window, getStorageKey(accountKey), JSON.stringify(state));
}

export function shouldShowRewardsTip(
    accountKey: string,
    owaSessionCount: number,
    isFeatureEnabled: boolean
): boolean {
    if (!accountKey || !isFeatureEnabled) {
        return false;
    }

    const state = getState(accountKey);
    if (state.hasOpenedRewards || state.impressionCount >= MAX_IMPRESSIONS) {
        return false;
    }

    const currentSessionNumber = owaSessionCount + 1;
    if (state.lastImpressionSession === currentSessionNumber) {
        return false;
    }

    if (state.impressionCount === 0) {
        return true;
    }

    return currentSessionNumber - state.lastImpressionSession >= SESSION_INTERVAL;
}

export function markRewardsTipShown(
    accountKey: string,
    owaSessionCount: number
): number | undefined {
    if (!accountKey) {
        return undefined;
    }

    const state = getState(accountKey);
    const currentSessionNumber = owaSessionCount + 1;
    if (
        state.hasOpenedRewards ||
        state.impressionCount >= MAX_IMPRESSIONS ||
        state.lastImpressionSession === currentSessionNumber
    ) {
        return undefined;
    }

    const impressionNumber = state.impressionCount + 1;
    setState(accountKey, {
        ...state,
        impressionCount: impressionNumber,
        lastImpressionSession: currentSessionNumber,
    });
    return impressionNumber;
}

export function markRewardsPanelOpened(accountKey: string): void {
    if (!accountKey) {
        return;
    }

    setRewardsPanelOpenedMutator(accountKey);
    const state = getState(accountKey);
    if (!state.hasOpenedRewards) {
        setState(accountKey, { ...state, hasOpenedRewards: true });
    }
}
